import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Breadcrumb, Button, Select, Table } from "antd";
import type { ColumnsType } from "antd/es/table";

import { adminHomeBcmb } from "src/static/data/breadcrumb_items";
import { MainContext } from "src/context";
import { Container } from "src/components/container/Container";
import { GetQuizRulesListConfig, GetReportExcelConfig, GetStatisticsUniversityGenderConfig, GetUniversitiesConfig } from "src/service/api";
import { QuizRules, StatisticsUniversityGenderTypes, UniversityTypes } from "src/static/interface";
import { catchFunc, openLink } from "src/utils";
import { DownloadOutlined } from "@ant-design/icons";

function AdminReportPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<StatisticsUniversityGenderTypes[]>([]);
  const quizRuleId = searchParams.get("quizRuleId") || undefined;
  const search = searchParams.get("search") || undefined;
  const universityCode = searchParams.get("universityCode") || undefined;
  const isCompleted = searchParams.get("isCompleted");
  const { handleMakeParams } = useContext(MainContext);
  const [quizRules, setQuizRules] = useState<QuizRules[]>();
  const [universities, setUniversities] = useState<UniversityTypes[]>([]);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const { Option } = Select;

  const getFunc = () => {
    if (!quizRuleId) {
      setDataSource([]);
      return;
    }

    setLoading(true);
    GetStatisticsUniversityGenderConfig({
      quizRuleId: Number(quizRuleId),
      search: search,
      universityCode: universityCode,
      isCompleted: isCompleted ? isCompleted === "true" : undefined,
    })
      .then((res) => {
        const data = res.data.map((item: any, index: number) => ({
          ...item,
          key: index + 1,
        }));
        setDataSource(data);
      })
      .catch((err) => catchFunc(err))
      .finally(() => setLoading(false));
  };

  const getFuncQuizRules = () => {
    GetQuizRulesListConfig().then((res) => {
      setQuizRules(res.data);

      // If no quizRuleId is selected, select the first one by default
      if (!quizRuleId && res.data && res.data.length > 0) {
        handleMakeParams?.("quizRuleId", String(res.data[0].id));
      }
    });
  };

  const getFuncUniversities = () => {
    GetUniversitiesConfig().then((res) => {
      setUniversities(res.data || []);
    }).catch((err) => catchFunc(err));
  };

  useEffect(() => {
    getFuncQuizRules();
    getFuncUniversities();
  }, []);

  useEffect(() => {
    getFunc();
  }, [quizRuleId, search, universityCode, isCompleted]);

  const downloadExcel = () => {
    if (!quizRuleId) return;
    
    setLoadingExcel(true);
    GetReportExcelConfig({
      quizRuleId: Number(quizRuleId),
      universityCode: universityCode,
      isCompleted: isCompleted ? isCompleted === "true" : undefined,
    })
      .then((res) => {
        openLink(
          URL.createObjectURL(res.blob),
          "Universitetlar kesimida hisobot excel"
        );
      })
      .catch((err) => catchFunc(err))
      .finally(() => setLoadingExcel(false));
  };


  const columns: ColumnsType<StatisticsUniversityGenderTypes> = [
    {
      title: "№",
      dataIndex: "key",
      key: "key",
      width: 60,
      align: "center",
      fixed: "left",
    },
    {
      title: "OTM nomi",
      dataIndex: "university",
      key: "university",
      width: 300,
      fixed: "left",
    },
    {
      title: "OTM kodi",
      dataIndex: "code",
      key: "code",
      width: 100,
      align: "center",
    },
    {
      title: "Jami ishtirokchilar",
      dataIndex: "total",
      key: "total",
      width: 120,
      align: "center",
      render: (val) => val?.toLocaleString(),
      sorter: (a, b) => (a.total || 0) - (b.total || 0),
    },
    {
      title: "Qatnashganlar",
      dataIndex: "participantTotal",
      key: "participantTotal",
      width: 120,
      align: "center",
      render: (val) => val?.toLocaleString(),
      sorter: (a, b) => (a.participantTotal || 0) - (b.participantTotal || 0),
    },
    {
      title: "Erkaklar",
      dataIndex: "participantMale",
      key: "participantMale",
      width: 100,
      align: "center",
      render: (val) => val?.toLocaleString(),
      sorter: (a, b) => (a.participantMale || 0) - (b.participantMale || 0),
    },
    {
      title: "Ayollar",
      dataIndex: "participantFemale",
      key: "participantFemale",
      width: 100,
      align: "center",
      render: (val) => val?.toLocaleString(),
      sorter: (a, b) => (a.participantFemale || 0) - (b.participantFemale || 0),
    },
    {
      title: "Ishtirok darajasi (%)",
      dataIndex: "participationRate",
      key: "participationRate",
      width: 150,
      align: "center",
      render: (val) => (
        <span style={{ fontWeight: 600, color: val > 70 ? "#10b981" : val > 40 ? "#f59e0b" : "#ef4444" }}>
          {val?.toFixed(1)}%
        </span>
      ),
      sorter: (a, b) => (a.participationRate || 0) - (b.participationRate || 0),
    },
  ];

  const handleChange = (val: string) => {
    handleMakeParams?.("quizRuleId", val || "");
  };

  const handleUniversityChange = (val: string) => {
    handleMakeParams?.("universityCode", val || "");
  };

  const handleIsCompletedChange = (val: string) => {
    handleMakeParams?.("isCompleted", val || "");
  };

  return (
    <div>
      <Breadcrumb
        items={[
          adminHomeBcmb,
          {
            title: "Hisobot",
          },
        ]}
      />
      <Container
        title="Universitetlar kesimida hisobot"
        extra={
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Select
              value={quizRuleId}
              allowClear={true}
              showSearch
              placeholder="So'rovnomani tanlang"
              onChange={handleChange}
              style={{ width: 300 }}
              optionFilterProp="children"
              filterOption={(input, option: any) => {
                const text = Array.isArray(option.children)
                  ? option.children.join("")
                  : option.children;
                return String(text || "").toLowerCase().includes(input.toLowerCase());
              }}
            >
              {quizRules?.map((item: any) => {
                const prefix = item.hemisUserType === "TEACHER"
                  ? "O'qituvchilar uchun: "
                  : item.hemisUserType === "STUDENT"
                    ? "Talabalar uchun: "
                    : "";
                return (
                  <Option key={item.id} value={String(item.id)}>
                    {prefix && <strong>{prefix}</strong>}
                    {item.theme}
                  </Option>
                );
              })}
            </Select>
            <Select
              value={universityCode}
              allowClear={true}
              showSearch
              placeholder="Universitetni tanlang"
              onChange={handleUniversityChange}
              style={{ width: 250 }}
              optionFilterProp="children"
              filterOption={(input, option: any) => {
                const text = Array.isArray(option.children)
                  ? option.children.join("")
                  : option.children;
                return String(text || "").toLowerCase().includes(input.toLowerCase());
              }}
            >
              {universities.map((item) => (
                <Option key={item.code} value={item.code}>
                  {item.name}
                </Option>
              ))}
            </Select>
            <Select
              value={isCompleted}
              allowClear={true}
              placeholder="Holatni tanlang"
              onChange={handleIsCompletedChange}
              style={{ width: 180 }}
            >
              <Option value="true">Yakunlangan</Option>
              <Option value="false">Jarayonda</Option>
            </Select>
            <Button
              onClick={downloadExcel}
              icon={<DownloadOutlined />}
              type="primary"
              disabled={!quizRuleId}
              className="excel"
              loading={loadingExcel}
            >
              Excel yuklash
            </Button>
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={false}
          scroll={{ x: 1000, y: "calc(100vh - 350px)" }}
          size="small"
          bordered
          sticky={{ offsetHeader: 0 }}
        />
      </Container>
    </div>
  );
}

export default AdminReportPage;
